import Select, { components, type ValueContainerProps } from 'react-select';
import { type CompanyDto } from '@/app/models/CompanyDto';
import { useActiveCompanies } from './core/_hooks';

type CompanySelectBaseProps = {
  className?: string;
  inputId: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  options?: CompanyDto[];
};

type CompanySelectSingleProps = CompanySelectBaseProps & {
  isMulti?: false;
  value?: CompanyDto | null;
  onChange: (company: CompanyDto | null) => void;
};

type CompanySelectMultiProps = CompanySelectBaseProps & {
  isMulti: true;
  /** Basta o `id`: o filtro restaurado da URL não traz os nomes, que vêm das empresas ativas. */
  value?: CompanyDto[];
  onChange: (companies: CompanyDto[]) => void;
};

type CompanySelectProps = CompanySelectSingleProps | CompanySelectMultiProps;

// Matriz e filial costumam ter o mesmo nome fantasia: o CNPJ é que as separa.
const companyLabel = (company: CompanyDto) => `${company.fantasy_name || company.social_name} · ${company.cnpj}`;

const companyCount = (count: number) => (count === 1 ? '1 empresa' : `${count} empresas`);

function CompanyCountContainer({ children, getValue, ...props }: Readonly<ValueContainerProps<CompanyDto, true>>) {
  const count = getValue().length;

  return (
    <components.ValueContainer {...props} getValue={getValue}>
      {count > 0 && <span className="text-primary">{companyCount(count)}</span>}
      {children}
    </components.ValueContainer>
  );
}

function CompanySelect({
  className,
  inputId,
  isClearable,
  isDisabled,
  isMulti,
  options,
  value,
  onChange,
}: Readonly<CompanySelectProps>) {
  const { data: activeCompanies = [], isPending } = useActiveCompanies();
  const companies = options ?? activeCompanies;
  const selectProps = {
    className: className ?? 'react-select-styled',
    classNamePrefix: 'react-select',
    getOptionLabel: companyLabel,
    getOptionValue: (company: CompanyDto) => company.id ?? '',
    inputId,
    isClearable,
    isDisabled,
    isLoading: isPending,
    noOptionsMessage: () => 'Nenhuma empresa ativa',
    options: companies,
    placeholder: 'Selecione a empresa',
  };

  if (isMulti) {
    return (
      <Select<CompanyDto, true>
        {...selectProps}
        isMulti
        components={{ ValueContainer: CompanyCountContainer }}
        hideSelectedOptions={false}
        styles={{ multiValue: (base) => ({ ...base, display: 'none' }) }}
        value={companies.filter((company) => value?.some((selected) => selected.id === company.id))}
        onChange={(selected) => onChange([...selected])}
      />
    );
  }

  return <Select<CompanyDto> {...selectProps} value={value ?? null} onChange={onChange} />;
}

export default CompanySelect;
